import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BandVariance, Grade, GradeTarget } from '@cfl/shared';

import { api, type ApiError } from '@/lib/api';

export interface CalibrationRow {
  entryId: string;
  /** Position in the stack rank. Derived from the score server-side, never stored. */
  rank: number;
  employeeId: string;
  name: string;
  departmentName: string | null;
  managerName: string | null;
  score: number;
  proposedGrade: Grade;
  proposedLabel: string;
  finalGrade: Grade;
  finalLabel: string;
  moved: boolean;
  reason: string | null;
  movedByName: string | null;
  movedAt: string | null;
  selfRating: number | null;
}

export interface CalibrationView {
  id: string;
  cycleId: string;
  cycleLabel: string;
  quarterId: string | null;
  quarterLabel: string | null;
  scope: 'QUARTER' | 'CYCLE';
  status: 'OPEN' | 'LOCKED';
  openedByName: string | null;
  openedAt: string;
  lockedByName: string | null;
  lockedAt: string | null;
  population: number;
  variance: BandVariance[];
  /** Moved rows still missing a reason. Locking is refused while this is non-zero. */
  missingReasons: number;
  rows: CalibrationRow[];
}

export interface CalibrationSummary {
  id: string;
  cycleId: string;
  cycleLabel: string;
  quarterId: string | null;
  quarterLabel: string | null;
  scope: 'QUARTER' | 'CYCLE';
  status: 'OPEN' | 'LOCKED';
  population: number;
  movedCount: number;
  openedAt: string;
  lockedAt: string | null;
}

export function useCalibrationSessions(cycleId: string | undefined) {
  return useQuery<CalibrationSummary[], ApiError>({
    queryKey: ['calibration', 'sessions', cycleId ?? ''],
    queryFn: () =>
      api.get<CalibrationSummary[]>(
        cycleId ? `/calibration/sessions?cycleId=${cycleId}` : '/calibration/sessions',
      ),
    enabled: Boolean(cycleId),
  });
}

export function useCalibration(sessionId: string | undefined) {
  return useQuery<CalibrationView, ApiError>({
    queryKey: ['calibration', 'session', sessionId ?? ''],
    queryFn: () => api.get<CalibrationView>(`/calibration/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
  });
}

/**
 * Every calibration write returns the whole panel, so the cache is replaced from the
 * response rather than recomputed. The variance depends on all six bands at once -
 * moving one person changes two of them - and a client-side recount is how the
 * screen ends up disagreeing with the server about whether the curve is met.
 */
function useCalibrationMutation<TArgs>(fn: (args: TArgs) => Promise<CalibrationView>) {
  const queryClient = useQueryClient();
  return useMutation<CalibrationView, ApiError, TArgs>({
    mutationFn: fn,
    onSuccess: (next) => {
      queryClient.setQueryData(['calibration', 'session', next.id], next);
      void queryClient.invalidateQueries({ queryKey: ['calibration', 'sessions'] });
      // Locking writes grades onto assessments, so anything showing a grade is stale.
      void queryClient.invalidateQueries({ queryKey: ['analytics'] });
      void queryClient.invalidateQueries({ queryKey: ['annual'] });
    },
  });
}

export function useOpenCalibration() {
  return useCalibrationMutation<{ cycleId: string; quarterId: string | null }>((body) =>
    api.post<CalibrationView>('/calibration/sessions', body),
  );
}

export function useMoveCalibrationEntry() {
  return useCalibrationMutation<{ entryId: string; grade: Grade; reason?: string }>(
    ({ entryId, ...body }) => api.patch<CalibrationView>(`/calibration/entries/${entryId}`, body),
  );
}

export function useLockCalibration() {
  return useCalibrationMutation<string>((sessionId) =>
    api.post<CalibrationView>(`/calibration/sessions/${sessionId}/lock`),
  );
}

export function useUnlockCalibration() {
  return useCalibrationMutation<string>((sessionId) =>
    api.post<CalibrationView>(`/calibration/sessions/${sessionId}/unlock`),
  );
}

export function useRatingTargets(cycleId: string | undefined) {
  return useQuery<GradeTarget[], ApiError>({
    queryKey: ['cycle', cycleId ?? '', 'targets'],
    queryFn: () => api.get<GradeTarget[]>(`/cycles/${cycleId}/targets`),
    enabled: Boolean(cycleId),
  });
}

export function useSetRatingTargets(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation<GradeTarget[], ApiError, GradeTarget[]>({
    mutationFn: (targets) =>
      api.put<GradeTarget[]>(`/cycles/${cycleId}/targets`, { targets }),
    onSuccess: (next) => {
      queryClient.setQueryData(['cycle', cycleId, 'targets'], next);
      // The variance on any open panel is measured against these.
      void queryClient.invalidateQueries({ queryKey: ['calibration'] });
    },
  });
}
